import { type ZodType } from 'zod';
import {
  RequestListResponseSchema,
  RequestSchema,
  type Request,
  type RequestListQuery,
  type RequestListResponse,
} from '@magimp/shared';
import { request } from '../client.js';
import { serializeFiltersToUrl } from './serializeFilters.js';
import { withQuery } from './_query.js';

// WHY: schemas containing a Zod-branded id (`RoleRefSchema.id` is
// `RoleIdSchema = z.string().uuid().brand<'RoleId'>()`) infer
// asymmetric input/output types — input keeps the raw string, output
// carries the brand. The strict `ZodType<T, _, T>` annotation
// `request<T>` requires therefore mismatches the `_input` of the
// branded schema. The runtime parser is correct (the brand is just a
// TS-level marker), so the documented mitigation is the
// `as unknown as ZodType<T>` cast at the boundary, kept here so the
// schema-author types stay accurate elsewhere.

export async function listRequests(
  filters: Partial<RequestListQuery> = {},
  opts: { signal?: AbortSignal } = {},
): Promise<RequestListResponse> {
  const qs = serializeFiltersToUrl(filters);
  const reqOpts = opts.signal ? { signal: opts.signal } : {};
  return request<RequestListResponse>(
    withQuery('/requests', qs),
    { method: 'GET' },
    reqOpts,
    RequestListResponseSchema as unknown as ZodType<RequestListResponse>,
  );
}

export function fetchRequest(
  id: string,
  opts: {
    signal?: AbortSignal;
    includeItemPackages?: boolean;
    includeBoxes?: boolean;
    includeBoxVersions?: boolean;
    includeFunnelLinks?: boolean;
  } = {},
): Promise<Request> {
  // WHY (#590): opt-in expanded shape — when true, the backend emits
  //   `dimensionsMode` + `packages` on every item, including persisted
  //   package rows. The editor needs this to faithfully re-open a
  //   dimensions-mode DRAFT/RETURN; legacy callers omit the flag and
  //   keep the stale-bundle-safe legacy shape.
  // WHY (#599): same gate for the boxes expand. When true, the backend
  //   emits the `boxes` array (with nested items); off by default so
  //   stale bundles parse the response cleanly.
  // WHY (#1178): same gate for `RequestBox.updatedAt`. The regroup PATCH
  //   needs the parent-box version token to build the `If-Match` header;
  //   only callers that intend to invoke regroup opt in. Pre-#1178 bundles
  //   never set the flag, never see the field, and stay strictly-parsed.
  // WHY (#1597): same opt-in gate for the funnel-link public codes
  //   (`leadPublicCode` / `dealPublicCode`). Only the drawer detail read
  //   (and the realtime writers sharing its cache key) request them; list
  //   reads omit the flag and keep the slim shape.
  const params: string[] = [];
  if (opts.includeItemPackages === true) params.push('includeItemPackages=true');
  if (opts.includeBoxes === true) params.push('includeBoxes=true');
  if (opts.includeBoxVersions === true) params.push('includeBoxVersions=true');
  if (opts.includeFunnelLinks === true) params.push('includeFunnelLinks=true');
  const qs = params.length > 0 ? `?${params.join('&')}` : '';
  return request<Request>(
    `/requests/${encodeURIComponent(id)}${qs}`,
    { method: 'GET' },
    opts.signal ? { signal: opts.signal } : {},
    RequestSchema as unknown as ZodType<Request>,
  );
}
